import { _isNil, _isNotEmpty, _notNil } from '@/littledash';
import type { components } from '@/generated/internal-api/openapi-types';
import type { StudyApiId } from '@/model/Study.model';

type TreatmentTypeGlossary = components['schemas']['TreatmentTypeGlossaryV1.schema'];
type TreatmentUpdatePayloadItem = components['schemas']['TreatmentTypeGlossaryUpdateItemV1.schema'];

interface ValidatableHotInstance {
  countRows?: () => number;
  validateCells?: (callback?: (valid: boolean) => void) => void;
  validateRows?: (rows: Array<number>, callback?: (valid: boolean) => void) => void;
}

interface ValidateGridOptions {
  /** Trailing spare rows to exclude, mirroring how Spreadsheet skips them on load. */
  spareRows?: number;
}

/**
 * Runs the grid's validators and resolves once every one of them — including the asynchronous
 * uniqueness check — has reported. Handsontable's completion callback is the only way to know
 * an async validator has settled, so a save that skips this can submit a name whose remote
 * check was still in flight.
 *
 * Resolves `true` when validation could not be run at all, so an unexpectedly shaped instance
 * blocks the save no more than it did before.
 */
export const validateGridBeforeSave = async (
  hotInstance: ValidatableHotInstance,
  { spareRows = 0 }: ValidateGridOptions = {}
): Promise<boolean> => {
  const rowCount = hotInstance.countRows?.();

  if (spareRows > 0 && _notNil(rowCount) && typeof hotInstance.validateRows === 'function') {
    const rowsToValidate = Array.from({ length: Math.max(0, rowCount - spareRows) }, (_row, index) => index);
    if (rowsToValidate.length === 0) {
      return true;
    }
    return await new Promise<boolean>((resolve) => {
      hotInstance.validateRows?.(rowsToValidate, resolve);
    });
  }

  if (typeof hotInstance.validateCells === 'function') {
    return await new Promise<boolean>((resolve) => {
      hotInstance.validateCells?.(resolve);
    });
  }

  return true;
};

/**
 * Counts the distinct studies whose historical data a bulk rename will rewrite, considering only
 * the treatments present in the update payload.
 *
 * `studies` is only populated when the list request asked for `include=studies`; a treatment
 * without it contributes nothing, so a caller gating a confirmation on this count falls back to
 * saving straight away rather than warning about a figure it cannot substantiate.
 */
export const countAffectedStudies = (
  treatments: Array<TreatmentTypeGlossary>,
  payload: Array<TreatmentUpdatePayloadItem>
): number => {
  const editedApiIds = new Set(payload.map(({ api_id }) => api_id));

  const studyApiIds = treatments.reduce<Set<StudyApiId>>((ids, treatment) => {
    if (editedApiIds.has(treatment.api_id)) {
      (treatment.studies ?? []).forEach(({ api_id }) => ids.add(api_id));
    }
    return ids;
  }, new Set());

  return studyApiIds.size;
};
